import { BALANCE } from '../balance';
import type { Condition } from '../schemas/progression';
import type { OrderTemplateDefInput } from '../schemas/progressionDefs';

/**
 * NPC order templates (content-schemas §5.3, story-dialogue §9, economy-progression §2.2).
 * First orders are scripted and use exact quantities; regular templates roll each line in its range and the
 * total is clamped to BALANCE.orderQty (2–4). "khởi đầu" templates need the Tiệm, the rest the matching product
 * tier. Reward and friendship use the balance defaults (×1.5, +10). Orders never give stars.
 */
const Q = BALANCE.orderQty;
const shop: Condition[] = [{ type: 'unlocked', unlockId: 'unl_minimart' }];
const t2: Condition[] = [{ type: 'unlocked', unlockId: 'unl_products_t2' }];
const t3: Condition[] = [{ type: 'unlocked', unlockId: 'unl_products_t3' }];
/** Hàng nhà làm orders wait until the player has cooked that dish once (minigames.md §4.11). */
const cooked = (level: number): Condition[] => [{ type: 'miniGameCleared', miniGameId: 'mg_cooking', level }];

export const ORDER_TEMPLATES = [
  // ---- Scripted first orders (story-dialogue §9.1): Bảo → Lan → Mít ----
  {
    id: 'ord_first_bao',
    npcId: 'npc_bao',
    isFirst: true,
    picks: [{ from: { itemIds: ['prod_instant_noodle'] }, qty: { min: 3, max: 3 } }],
    totalQty: { min: 3, max: 3 },
    textKey: 'order.ord_first_bao.text',
    thanksKey: 'order.ord_first_bao.thanks',
    conditions: [],
    weight: 1,
  },
  {
    id: 'ord_first_lan',
    npcId: 'npc_lan',
    isFirst: true,
    picks: [
      { from: { itemIds: ['prod_cookie_box'] }, qty: { min: 2, max: 2 } },
      { from: { itemIds: ['prod_milk_tea'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: { min: 3, max: 3 },
    textKey: 'order.ord_first_lan.text',
    thanksKey: 'order.ord_first_lan.thanks',
    conditions: [],
    weight: 1,
  },
  {
    id: 'ord_first_mit',
    npcId: 'npc_mit',
    isFirst: true,
    picks: [
      { from: { itemIds: ['prod_candy_jar'] }, qty: { min: 2, max: 2 } },
      { from: { itemIds: ['prod_milk_tea'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: { min: 3, max: 3 },
    textKey: 'order.ord_first_mit.text',
    thanksKey: 'order.ord_first_mit.thanks',
    conditions: [],
    weight: 1,
  },

  // ---- Cô Lan ----
  {
    id: 'ord_lan_tea_party',
    npcId: 'npc_lan',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_cookie_box'] }, qty: { min: 1, max: 2 } },
      { from: { itemIds: ['prod_milk_tea'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_lan_tea_party.text',
    thanksKey: 'order.ord_lan_tea_party.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_lan_green_tea',
    npcId: 'npc_lan',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_green_tea'] }, qty: { min: 2, max: 3 } },
      { from: { itemIds: ['prod_cookie_box'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_lan_green_tea.text',
    thanksKey: 'order.ord_lan_green_tea.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_lan_garden',
    npcId: 'npc_lan',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_seed_packet'] }, qty: { min: 2, max: 3 } }],
    totalQty: Q,
    textKey: 'order.ord_lan_garden.text',
    thanksKey: 'order.ord_lan_garden.thanks',
    conditions: t3,
    weight: 1,
  },
  {
    id: 'ord_lan_honey_jam',
    npcId: 'npc_lan',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_honey_jar'] }, qty: { min: 1, max: 1 } },
      { from: { itemIds: ['prod_strawberry_jam'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_lan_honey_jam.text',
    thanksKey: 'order.ord_lan_honey_jam.thanks',
    conditions: t3,
    weight: 1,
  },
  {
    id: 'ord_lan_visit_friend',
    npcId: 'npc_lan',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_fruit_basket'] }, qty: { min: 1, max: 2 } },
      { from: { itemIds: ['prod_green_tea'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_lan_visit_friend.text',
    thanksKey: 'order.ord_lan_visit_friend.thanks',
    conditions: t3,
    weight: 1,
  },

  // ---- Anh Bảo ----
  {
    id: 'ord_bao_deadline_fuel',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_canned_coffee'] }, qty: { min: 2, max: 3 } },
      { from: { itemIds: ['prod_instant_noodle'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_bao_deadline_fuel.text',
    thanksKey: 'order.ord_bao_deadline_fuel.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_bao_midnight',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_instant_noodle'] }, qty: { min: 2, max: 4 } }],
    totalQty: Q,
    textKey: 'order.ord_bao_midnight.text',
    thanksKey: 'order.ord_bao_midnight.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_bao_snack_meeting',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_cookie_box'] }, qty: { min: 1, max: 2 } },
      { from: { itemIds: ['prod_candy_jar'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_bao_snack_meeting.text',
    thanksKey: 'order.ord_bao_snack_meeting.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_bao_monday',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_canned_coffee'] }, qty: { min: 3, max: 4 } }],
    totalQty: Q,
    textKey: 'order.ord_bao_monday.text',
    thanksKey: 'order.ord_bao_monday.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_bao_notebook',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_pastel_notebook'] }, qty: { min: 1, max: 1 } },
      { from: { itemIds: ['prod_gel_pens'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_bao_notebook.text',
    thanksKey: 'order.ord_bao_notebook.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_bao_healthy_try',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_green_tea'] }, qty: { min: 2, max: 2 } },
      { from: { itemIds: ['prod_instant_noodle'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_bao_healthy_try.text',
    thanksKey: 'order.ord_bao_healthy_try.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_bao_laptop_sticker',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_sticker_pack'] }, qty: { min: 2, max: 3 } }],
    totalQty: Q,
    textKey: 'order.ord_bao_laptop_sticker.text',
    thanksKey: 'order.ord_bao_laptop_sticker.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_bao_candle',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_scented_candle'] }, qty: { min: 1, max: 1 } },
      { from: { itemIds: ['prod_canned_coffee'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_bao_candle.text',
    thanksKey: 'order.ord_bao_candle.thanks',
    conditions: t3,
    weight: 1,
  },

  // ---- Bé Mít ----
  {
    id: 'ord_mit_candy_share',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_candy_jar'] }, qty: { min: 2, max: 3 } }],
    totalQty: Q,
    textKey: 'order.ord_mit_candy_share.text',
    thanksKey: 'order.ord_mit_candy_share.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_mit_reward',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_milk_tea'] }, qty: { min: 1, max: 2 } },
      { from: { itemIds: ['prod_cookie_box'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_mit_reward.text',
    thanksKey: 'order.ord_mit_reward.thanks',
    conditions: shop,
    weight: 1,
  },
  {
    id: 'ord_mit_sticker_hunt',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_sticker_pack'] }, qty: { min: 2, max: 4 } }],
    totalQty: Q,
    textKey: 'order.ord_mit_sticker_hunt.text',
    thanksKey: 'order.ord_mit_sticker_hunt.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_mit_school',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_gel_pens'] }, qty: { min: 1, max: 2 } },
      { from: { itemIds: ['prod_pastel_notebook'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_mit_school.text',
    thanksKey: 'order.ord_mit_school.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_mit_cat_gift',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_cat_pate'] }, qty: { min: 1, max: 2 } },
      { from: { itemIds: ['prod_candy_jar'] }, qty: { min: 1, max: 1 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_mit_cat_gift.text',
    thanksKey: 'order.ord_mit_cat_gift.thanks',
    conditions: t2,
    weight: 1,
  },
  {
    id: 'ord_mit_science',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [{ from: { itemIds: ['prod_seed_packet'] }, qty: { min: 2, max: 2 } }],
    totalQty: Q,
    textKey: 'order.ord_mit_science.text',
    thanksKey: 'order.ord_mit_science.thanks',
    conditions: t3,
    weight: 1,
  },

  // ---- Hàng nhà làm (minigames.md §4.11): one home-made dish plus a little shop stock ----
  {
    id: 'ord_lan_home_jam',
    npcId: 'npc_lan',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_home_jam'] }, qty: { min: 1, max: 1 } },
      { from: { itemIds: ['prod_green_tea', 'prod_cookie_box'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_lan_home_jam.text',
    thanksKey: 'order.ord_lan_home_jam.thanks',
    conditions: cooked(1),
    weight: 1,
  },
  {
    id: 'ord_bao_home_noodle',
    npcId: 'npc_bao',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_home_noodle'] }, qty: { min: 1, max: 1 } },
      { from: { itemIds: ['prod_canned_coffee'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_bao_home_noodle.text',
    thanksKey: 'order.ord_bao_home_noodle.thanks',
    conditions: cooked(2),
    weight: 1,
  },
  {
    id: 'ord_mit_home_kimbap',
    npcId: 'npc_mit',
    isFirst: false,
    picks: [
      { from: { itemIds: ['prod_home_kimbap'] }, qty: { min: 1, max: 1 } },
      { from: { itemIds: ['prod_milk_tea', 'prod_candy_jar'] }, qty: { min: 1, max: 2 } },
    ],
    totalQty: Q,
    textKey: 'order.ord_mit_home_kimbap.text',
    thanksKey: 'order.ord_mit_home_kimbap.thanks',
    conditions: cooked(3),
    weight: 1,
  },
] as const satisfies readonly OrderTemplateDefInput[];
